import React from 'react';
import { logClientError } from '../../utils/clientErrorLog';
import { isChunkError, triggerChunkRecovery } from '../../utils/chunkRecovery';
import i18n from '../../i18n';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // EV-845 — a React.lazy dynamic-import failure (stale cached shell vs deployed
    // chunks) lands HERE, not on window.onerror. Auto-recover (clear caches + SW +
    // hard reload) instead of showing the dead-end "Oops" page.
    if (isChunkError(error?.message)) {
      try {
        logClientError({ kind: 'chunk_error', error_name: error?.name || 'Error', error_message: error?.message || String(error) });
      } catch { /* ignore */ }
      triggerChunkRecovery();
      return;
    }
    this.setState({ errorInfo });
    // EV-839 — ship the crash to Neon so we can see WHICH page / user / build broke.
    try {
      logClientError({
        kind: 'crash',
        error_name: error?.name || 'Error',
        error_message: error?.message || String(error),
        error_stack: error?.stack || null,
        component_stack: errorInfo?.componentStack || null,
      });
    } catch { /* never cascade */ }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#fef3c7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          padding: '16px'
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '448px',
            width: '100%'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              padding: '32px'
            }}>
              <div style={{
                fontSize: '64px',
                marginBottom: '16px',
                lineHeight: 1
              }}>
                😓
              </div>
              
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '8px',
                margin: '0 0 8px 0'
              }}>
                {i18n.t('common.oopsTitle')}
              </h2>

              <p style={{
                color: '#6b7280',
                marginBottom: '16px',
                fontSize: '14px',
                lineHeight: '1.5',
                margin: '0 0 16px 0'
              }}>
                {i18n.t('common.oopsBody')}
              </p>

              {/* DEBUG error details — auto-shown in dev/staging/?debug=1, OR on demand
                  via the "Show error details" button below (Patrick: typing ?debug=1 on a
                  phone is painful — give a tap button instead). */}
              {this.state.error && typeof window !== 'undefined' &&
                (this.state.showDetails ||
                 window.location.hostname.includes('localhost') ||
                 window.location.hostname.includes('staging.') ||
                 window.location.search.includes('debug=1') ||
                 window.location.hash.includes('debug')) && (
                <div style={{
                  textAlign: 'left', background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: '8px', padding: '10px', marginBottom: '16px', fontSize: '11px',
                  fontFamily: 'monospace', color: '#7f1d1d', whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word', maxHeight: '240px', overflowY: 'auto',
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {this.state.error.name}: {this.state.error.message}
                  </div>
                  {this.state.error.stack && (
                    <div style={{ opacity: 0.8 }}>
                      {String(this.state.error.stack).split('\n').slice(0, 6).join('\n')}
                    </div>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <div style={{ opacity: 0.7, marginTop: '6px' }}>
                      {String(this.state.errorInfo.componentStack).split('\n').slice(0, 6).join('\n')}
                    </div>
                  )}
                </div>
              )}
              
              {/* Primary escape = go HOME. Refreshing a crashed route just re-crashes
                  it in a loop, which is the trap. Home always renders. (Patrick P0) */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => { window.location.href = '/'; }}
                  style={{
                    backgroundColor: '#E46E34', color: 'white', padding: '12px 24px',
                    borderRadius: '12px', fontWeight: 700, border: 'none', cursor: 'pointer',
                    fontSize: '14px', boxShadow: '0 4px 6px rgba(228, 110, 52, 0.25)',
                  }}
                >
                  🏠 {i18n.t('common.goToHomepage')}
                </button>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    backgroundColor: '#fff', color: '#92400e', padding: '12px 20px',
                    borderRadius: '12px', fontWeight: 600, border: '1px solid #fcd34d',
                    cursor: 'pointer', fontSize: '14px',
                  }}
                >
                  {i18n.t('common.reload')}
                </button>
              </div>
              {!this.state.showDetails && (
                <button
                  onClick={() => this.setState({ showDetails: true })}
                  style={{
                    marginTop: '14px', background: 'none', border: 'none', cursor: 'pointer',
                    color: '#9ca3af', fontSize: '12px', fontWeight: 600, textDecoration: 'underline',
                  }}
                >
                  🐞 {i18n.t('common.showErrorDetails')}
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
